<script setup lang="ts">
/**
 * Système de modales du projet, sans `ion-modal`.
 *
 * L'API ne change pas d'un iota : `is-open`, `mode`, `breakpoints`,
 * `initial-breakpoint`, `expand-to-scroll`, `backdrop-dismiss`,
 * `presenting-element`, les évènements `did-dismiss` / `did-present` et les
 * méthodes exposées `dismiss()` / `el()`. Les 38 appelants ne bougent pas.
 *
 * Trois modes, deux mécaniques :
 *
 * - `centered` est un dialogue reka ordinaire.
 * - `sheet` et `card` sont la MÊME chose : un panneau qui monte du bas et
 *   qu'on renvoie en le tirant vers le bas. Seules la hauteur et la mise en
 *   scène de la page derrière changent.
 *
 * Le geste de retrait part de N'IMPORTE OÙ dans le panneau, comme chez Ionic.
 * La règle qui rend ça vivable : si le doigt se pose dans une zone défilante
 * qui n'est pas en haut, c'est elle qui bouge, pas la feuille. Une fois qu'elle
 * a atteint son sommet, il faut relever le doigt et recommencer, ce que fait
 * aussi le système.
 *
 * ⚠️ La décision se prend au PREMIER mouvement. Une fois que le navigateur a
 * commencé à faire défiler, `preventDefault` ne le rattrape plus.
 *
 * ⚠️ `presentingElement` est capturé à l'OUVERTURE et conservé jusqu'à la fin
 * de la fermeture. Le lire au moment du retrait donnait une référence déjà
 * changée, et la page restait rétrécie.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, VisuallyHidden } from 'reka-ui'

const props = withDefaults(defineProps<{
  /** Ouverture contrôlée (v-model:is-open). */
  isOpen: boolean
  /**
   * 'sheet' = bas d'écran, poignée + tirer pour fermer ;
   * 'centered' = dialogue centré ;
   * 'card' = carte empilée façon iOS (la page derrière recule) ;
   * 'fullscreen' = plein écran sans arrondi (célébrations, formulaire d'avis).
   */
  mode?: 'sheet' | 'centered' | 'card' | 'fullscreen'
  /**
   * Le panneau ne peint aucun fond : l'appelant rend sa propre carte.
   * Remplace les `--background: transparent` posés sur l'ion-modal.
   */
  transparent?: boolean
  /**
   * Fondu au lieu de la montée. Pour les décors qui apparaissent sur place
   * (l'ouverture du coffre), où une montée ferait bouger la scène.
   */
  fade?: boolean
  /** Paliers du sheet. `[0, x]` = un seul palier à x ; `[0, x, y]` = deux crans. */
  breakpoints?: number[]
  /** Palier initial (mode sheet). */
  initialBreakpoint?: number
  /** Conservé pour compatibilité d'API ; le panneau ne grandit plus au défilement. */
  expandToScroll?: boolean
  /** Fermeture au toucher du fond. */
  backdropDismiss?: boolean
  /** Page à faire reculer en mode 'card'. */
  presentingElement?: HTMLElement
  /** Titre lu par les lecteurs d'écran (les appelants rendent le leur dans le contenu). */
  ariaLabel?: string
  /**
   * Classes posées sur le PANNEAU, et non sur le composant : `DialogRoot` ne
   * rend aucun élément, un `class` sur `<AppSheet>` ne se poserait nulle part.
   * C'est par là que passent les tailles sur mesure des anciens `ion-modal`.
   */
  panelClass?: string
  /** Idem pour le voile (les fonds floutés des célébrations). */
  backdropClass?: string
}>(), {
  mode: 'sheet',
  breakpoints: () => [0, 1],
  initialBreakpoint: 1,
  expandToScroll: false,
  backdropDismiss: true,
  presentingElement: undefined,
  ariaLabel: undefined,
  transparent: false,
  fade: false,
  panelClass: undefined,
  backdropClass: undefined,
})

const emit = defineEmits<{
  'update:isOpen': [v: boolean]
  /** Fin de l'animation de fermeture : permet d'enchaîner un autre overlay sans collision. */
  'did-dismiss': []
  /** Panneau ouvert : ex. focus d'un champ. */
  'did-present': []
}>()

/** Durée des transitions d'ouverture et de fermeture, alignée sur le CSS. */
const ANIM_MS = 280
/** Fraction de la hauteur du panneau au-delà de laquelle on ferme au relâchement. */
const DISMISS_RATIO = 0.35
/** Vitesse (px/ms) qui ferme quel que soit le chemin parcouru. */
const DISMISS_VELOCITY = 0.5
/**
 * Retour en place quand la traction n'a pas suffi.
 *
 * Le panneau n'avait AUCUNE transition : il n'a que des animations d'entrée, et
 * relâcher sous le seuil le téléportait à sa place. Le voile revient sur la
 * même durée et la même courbe, celle de la montée : le geste doit se défaire
 * comme il s'est fait, d'un seul mouvement.
 */
const SETTLE_MS = 260
const SETTLE_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'

const contentRef = ref<InstanceType<typeof DialogContent> | null>(null)
const dragOffset = ref(0)
const dragging = ref(false)
/**
 * Hauteur du panneau, mesurée UNE fois par geste, au poser du doigt.
 *
 * Elle sert au seuil de fermeture et au recul du voile. La relire à chaque
 * image forcerait un recalcul de mise en page en plein glissement, c'est-à-dire
 * exactement au moment où il ne faut en demander aucun.
 */
const dragHeight = ref(0)
/** Le panneau revient à sa place : le temps de la course, il s'anime. */
const settling = ref(false)
let settleTimer: ReturnType<typeof setTimeout> | null = null

function settleBack() {
  if (settleTimer) clearTimeout(settleTimer)
  settling.value = true
  dragOffset.value = 0
  settleTimer = setTimeout(() => { settling.value = false; settleTimer = null }, SETTLE_MS)
}

/** Paliers utiles (on retire le 0, qui veut dire « fermé » chez Ionic). */
const stops = computed(() => {
  const list = (props.breakpoints ?? [0, 1]).filter((b) => b > 0).sort((a, b) => a - b)
  return list.length ? list : [1]
})
const currentStop = ref(props.initialBreakpoint > 0 ? props.initialBreakpoint : 1)

// Seul le mode `sheet` a une hauteur propre : la carte est bornée par
// `top`/`bottom`, le dialogue centré par son contenu.
//
// Le palier s'applique à la hauteur DISPONIBLE, pas à la fenêtre : une feuille
// à 1 doit s'arrêter sous la barre d'état, comme la carte, sinon son contenu
// passe derrière l'encoche. C'est exactement ce que fait `.modal-sheet` chez
// Ionic (`calc(100% - safe-area-top - 10px)`).
const panelHeight = computed(() => {
  const stop = Math.min(1, Math.max(0.1, currentStop.value))
  return `calc((100% - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) - 10px) * ${stop})`
})

function contentEl(): HTMLElement | undefined {
  return (contentRef.value?.$el as HTMLElement | undefined) ?? undefined
}

// ── Mise en scène de la page derrière, en mode carte ────────────────────────
// Capturée à l'ouverture : la relire à la fermeture donnait une référence déjà
// changée, et la page restait rétrécie.
let stagedEl: HTMLElement | null = null

// Les valeurs viennent de l'animation d'entrée iOS d'Ionic, reprises telles
// quelles : échelle 0,915, origine en haut au centre, et une translation de
// `max(30px, safe-area-top)` — sauf au-dessus d'une autre carte, où la page
// recule seulement de 10px pour que l'empilement reste lisible.
const PRESENTING_SCALE = 0.915
const PRESENTING_RADIUS = '10px'

function stagePresenting() {
  if (props.mode !== 'card' || !(props.presentingElement instanceof HTMLElement)) return
  stagedEl = props.presentingElement
  const overCard = stagedEl.closest('.app-sheet-panel') !== null
  const offset = overCard
    ? '-10px'
    : 'max(30px, var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))'

  stagedEl.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.32, 0.72, 0, 1), border-radius ${ANIM_MS}ms ease`
  stagedEl.style.transformOrigin = 'top center'
  stagedEl.style.overflow = 'hidden'
  stagedEl.style.borderRadius = PRESENTING_RADIUS
  stagedEl.style.transform = `translateY(${offset}) scale(${PRESENTING_SCALE})`

  // Le noir derrière la page rétrécie, c'est lui qui donne la profondeur : sans
  // lui on voit le fond crème de la page et la carte semble simplement décalée.
  document.body.style.backgroundColor = '#000'
}

function unstagePresenting() {
  const el = stagedEl
  if (!el) return
  stagedEl = null
  el.style.transform = ''
  el.style.borderRadius = ''
  window.setTimeout(() => {
    el.style.transition = ''
    el.style.transformOrigin = ''
    el.style.overflow = ''
    document.body.style.backgroundColor = ''
  }, ANIM_MS)
}

// ── Geste de retrait ────────────────────────────────────────────────────────
// Le geste part de N'IMPORTE OÙ dans le panneau, poignée comprise, exactement
// comme chez Ionic. La règle qui rend ça vivable : si le doigt s'est posé dans
// une zone défilante qui n'est PAS en haut, c'est elle qui bouge, pas la
// feuille. Une fois qu'elle a atteint son sommet, il faut relever le doigt et
// recommencer, ce que fait aussi le système.
//
// La décision se prend au PREMIER mouvement, et pas après : une fois que le
// navigateur a commencé à faire défiler, `preventDefault` ne le rattrape plus.
/** Course en pixels avant qu'un mouvement compte comme une traction. */
const DRAG_THRESHOLD = 6

let startY = 0
let startX = 0
let startTime = 0
let dragScroller: HTMLElement | null = null
let armed = false

// ── Diagnostic des taps perdus (HARMONIA-92) ───────────────────────────────
// Amplitude compte un « dead click » à chaque `pointerdown` qui n'est suivi
// d'aucune mutation du DOM dans les 3 s : un défilement commencé le pouce posé
// sur une suggestion en est un. Ses chiffres ne disent donc pas si un vrai tap
// se perd. Ce relevé-là ne retient que les vrais taps (doigt quasi immobile,
// relevé vite) posés sur un élément cliquable, et signale ceux qu'aucun clic
// n'a suivis. Il ne change rien au comportement : il observe.
/** Course maximale, en px, pour qu'un contact compte comme un tap. */
const TAP_SLOP = 10
/** Durée maximale d'un tap, en ms. */
const TAP_MAX_MS = 500
/** Délai laissé au navigateur pour émettre le clic après le relâchement. */
const TAP_CLICK_GRACE_MS = 600

interface TapProbe {
  target: Element
  maxDx: number
  maxDy: number
  dragEngaged: boolean
  scrollTop: number | null
}
let tapProbe: TapProbe | null = null
let pendingTap: (TapProbe & { durationMs: number }) | null = null
let pendingTapTimer: ReturnType<typeof setTimeout> | null = null

function clearPendingTap() {
  if (pendingTapTimer) clearTimeout(pendingTapTimer)
  pendingTapTimer = null
  pendingTap = null
}

function reportLostTap(tap: TapProbe & { durationMs: number }) {
  const testid = tap.target.closest('[data-testid]')?.getAttribute('data-testid') ?? null
  // Import différé : la feuille sert partout, y compris dans des tests qui ne
  // montent pas l'analytique.
  void import('@/composables/useAnalytics').then(({ useAnalytics }) => {
    void useAnalytics().logSheetTapLost({
      sheetMode: props.mode,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      targetTestid: testid,
      travelX: Math.round(tap.maxDx),
      travelY: Math.round(tap.maxDy),
      dragEngaged: tap.dragEngaged,
      scrollTop: tap.scrollTop,
      durationMs: tap.durationMs,
    })
  }).catch(() => { /* diagnostic seulement */ })
}

/** Un clic est arrivé : le tap n'est pas perdu. */
function onPanelClick(ev: MouseEvent) {
  if (pendingTap && ev.target instanceof Node && pendingTap.target.contains(ev.target)) clearPendingTap()
}

/** Remonte la première zone défilante entre la cible et le panneau. */
function scrollableUnder(target: EventTarget | null): HTMLElement | null {
  const panel = contentEl()
  let el = target instanceof HTMLElement ? target : null
  while (el && panel && panel.contains(el)) {
    const overflowY = getComputedStyle(el).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el
    el = el.parentElement
  }
  return null
}

function onDragStart(ev: TouchEvent) {
  // Ni le dialogue centré ni le plein écran ne se tirent : le premier n'a pas
  // de bord bas à saisir, le second sert aux célébrations et au formulaire
  // d'avis, que leurs appelants montent justement en non-renvoyables.
  if (props.mode !== 'sheet' && props.mode !== 'card') return
  if (ev.touches.length > 1) {
    armed = false
    return
  }
  startY = ev.touches[0].clientY
  startX = ev.touches[0].clientX
  startTime = Date.now()
  // Reprendre la feuille pendant qu'elle revient doit la rendre au doigt tout
  // de suite : une transition encore en place ferait traîner le panneau
  // derrière le doigt pendant un quart de seconde.
  if (settleTimer) { clearTimeout(settleTimer); settleTimer = null }
  settling.value = false
  dragHeight.value = contentEl()?.getBoundingClientRect().height || window.innerHeight
  dragScroller = scrollableUnder(ev.target)
  const tappable = ev.target instanceof Element ? ev.target.closest('button, a, [role="button"]') : null
  tapProbe = tappable && contentEl()?.contains(tappable)
    ? { target: tappable, maxDx: 0, maxDy: 0, dragEngaged: false, scrollTop: dragScroller ? Math.round(dragScroller.scrollTop) : null }
    : null
  armed = true
  dragging.value = false
  dragOffset.value = 0
}

function onDragMove(ev: TouchEvent) {
  if (tapProbe && ev.touches.length === 1) {
    tapProbe.maxDx = Math.max(tapProbe.maxDx, Math.abs(ev.touches[0].clientX - startX))
    tapProbe.maxDy = Math.max(tapProbe.maxDy, Math.abs(ev.touches[0].clientY - startY))
  }
  if (!armed || ev.touches.length > 1) return
  const dy = ev.touches[0].clientY - startY
  const dx = ev.touches[0].clientX - startX

  if (!dragging.value) {
    if (Math.abs(dy) < DRAG_THRESHOLD && Math.abs(dx) < DRAG_THRESHOLD) return
    // Plus horizontal que vertical : on laisse passer (carrousels, curseurs).
    if (Math.abs(dx) > Math.abs(dy)) {
      armed = false
      return
    }
    // Le contenu n'est pas en haut : c'est à lui de défiler.
    if (dy > 0 && dragScroller && dragScroller.scrollTop > 0) {
      armed = false
      return
    }
    // Vers le haut alors qu'il n'y a plus de cran au-dessus : rien à faire.
    if (dy < 0 && stops.value[stops.value.length - 1] <= currentStop.value) {
      armed = false
      return
    }
    dragging.value = true
    if (tapProbe) tapProbe.dragEngaged = true
  }

  // Vers le haut : on résiste au lieu de suivre, sauf s'il reste un cran.
  if (dy < 0 && stops.value[stops.value.length - 1] <= currentStop.value) {
    dragOffset.value = dy / 4
    return
  }
  if (ev.cancelable) ev.preventDefault()
  dragOffset.value = dy
}

function onDragEnd() {
  const probe = tapProbe
  tapProbe = null
  if (probe) {
    const durationMs = Date.now() - startTime
    if (Math.max(probe.maxDx, probe.maxDy) <= TAP_SLOP && durationMs <= TAP_MAX_MS) {
      clearPendingTap()
      pendingTap = { ...probe, durationMs }
      pendingTapTimer = setTimeout(() => {
        const tap = pendingTap
        clearPendingTap()
        if (tap) reportLostTap(tap)
      }, TAP_CLICK_GRACE_MS)
    }
  }
  armed = false
  dragScroller = null
  if (!dragging.value) return
  dragging.value = false
  const height = dragHeight.value || window.innerHeight
  const distance = dragOffset.value
  const velocity = distance / Math.max(1, Date.now() - startTime)

  if (distance > height * DISMISS_RATIO || velocity > DISMISS_VELOCITY) {
    dragOffset.value = 0
    close()
    return
  }

  // Cran supérieur atteint en tirant vers le haut : on y monte.
  if (distance < -60) {
    const next = stops.value.find((s) => s > currentStop.value)
    if (next) currentStop.value = next
  }
  settleBack()
}

/**
 * Le navigateur a repris le contact (défilement natif, geste système) : ce
 * n'était pas un tap, il n'y a rien à mesurer.
 */
function onDragCancel() {
  tapProbe = null
  onDragEnd()
}

const panelStyle = computed(() => ({
  height: props.mode === 'sheet' ? panelHeight.value : undefined,
  transform: dragOffset.value ? `translateY(${Math.max(0, dragOffset.value)}px)` : undefined,
  // Rien pendant le geste : le panneau colle au doigt. La transition
  // n'apparaît que pour le retour, et repart aussitôt après.
  transition: dragging.value
    ? 'none'
    : settling.value ? `transform ${SETTLE_MS}ms ${SETTLE_EASING}` : undefined,
}))

/**
 * Le voile recule à mesure qu'on tire la feuille vers le bas : au tiers du
 * chemin, l'écran de dessous est déjà au tiers redevenu net. Le geste rend la
 * page, il ne fait pas que déplacer un panneau.
 *
 * On fait varier l'OPACITÉ, jamais le rayon du flou. Deux raisons, et la
 * seconde est la plus importante :
 *
 * 1. un `backdrop-filter` dont le rayon bouge refiltre tout l'écran à chaque
 *    image, et ça se sent pendant un geste ; l'opacité reste au compositeur ;
 * 2. le voile fond alors avec le fond NET, ce qui se lit exactement comme un
 *    flou qui faiblit. C'est déjà le mécanisme de l'entrée
 *    (`app-sheet-backdrop-in` n'anime rien d'autre), donc le retrait est le
 *    miroir de l'arrivée, et les voiles qui déclarent leur propre flou
 *    (coffre, célébrations) reculent sans avoir à le savoir.
 */
const veilStyle = computed(() => {
  const travel = dragOffset.value
  if (travel <= 0) {
    // Le voile ne revient pas d'un bloc pendant que le panneau glisse : sans
    // cette transition-là, il retrouverait sa force avant que la feuille soit
    // rentrée, et le geste se terminerait en deux temps.
    return settling.value ? { opacity: '1', transition: `opacity ${SETTLE_MS}ms ${SETTLE_EASING}` } : undefined
  }
  const progress = Math.min(1, travel / (dragHeight.value || window.innerHeight))
  return { opacity: String(1 - progress) }
})

// ── Cycle d'ouverture ───────────────────────────────────────────────────────
let dismissTimer: ReturnType<typeof setTimeout> | null = null

/**
 * L'ouverture RÉELLE du panneau.
 *
 * Elle suit `isOpen`, mais la feuille peut aussi la baisser toute seule quand
 * on tape le fond, qu'on la tire vers le bas ou qu'on presse Échap. C'est le
 * contrat d'`ion-modal`, qu'on remplace : il se fermait de lui-même PUIS
 * prévenait par `didDismiss`.
 *
 * ⚠️ Sans ça, une feuille purement contrôlée reste ouverte pour toujours dès
 * que son appelant n'écoute pas `update:is-open`. Sept modales de l'app
 * étaient dans ce cas, toutes écrites du temps d'Ionic où `did-dismiss`
 * suffisait : menu contextuel, série, profil, jours d'absence, sélecteur
 * d'icône, note de la chouette, célébration du bilan. Le fond ne répondait
 * pas, et le glissé vers le bas ramenait le panneau en place.
 */
const localOpen = ref(props.isOpen)
watch(() => props.isOpen, (open) => { localOpen.value = open })

function close() {
  // On se ferme, PUIS on le dit : l'appelant qui pilote `isOpen` reste maître
  // de son état, celui qui n'écoute que `did-dismiss` n'est plus coincé.
  localOpen.value = false
  emit('update:isOpen', false)
}

function onOpenChange(open: boolean) {
  if (!open) close()
}

// Le cycle suit l'ouverture RÉELLE, pas la prop : sinon une fermeture décidée
// ici ne jouerait ni la sortie ni `did-dismiss`, et la baisse de `isOpen` qui
// suivrait les rejouerait une seconde fois.
//
// Pas d'`immediate` : au montage, la feuille est fermée et la branche de
// fermeture aurait émis un `did-dismiss` fantôme 280 ms après l'arrivée, que
// les appelants qui enchaînent un autre overlay auraient pris pour vrai.
watch(localOpen, async (open) => {
  if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null }
  if (open) {
    currentStop.value = props.initialBreakpoint > 0 ? props.initialBreakpoint : 1
    dragOffset.value = 0
    stagePresenting()
    await nextTick()
    // `did-present` après l'animation, comme le faisait Ionic : les appelants
    // s'en servent pour donner le focus, et focaliser pendant l'animation
    // fait sauter la vue sur iOS.
    dismissTimer = setTimeout(() => emit('did-present'), ANIM_MS)
    return
  }
  unstagePresenting()
  dismissTimer = setTimeout(() => emit('did-dismiss'), ANIM_MS)
})

onBeforeUnmount(() => {
  if (dismissTimer) clearTimeout(dismissTimer)
  if (settleTimer) clearTimeout(settleTimer)
  clearPendingTap()
  unstagePresenting()
})

/** Ferme programmatiquement (motif pendingAct côté appelant). */
function dismiss() {
  close()
}

/**
 * Élément DOM du panneau. Sert de `presenting-element` aux modales enfant, donc
 * il doit rester l'élément réellement affiché.
 */
function el(): HTMLElement | undefined {
  // `$el` d'un contenu à plusieurs racines est le nœud texte qui ouvre le
  // fragment, pas un élément : la feuille posée par-dessus (le choix d'icône
  // sur le formulaire de tâche) recevait ce nœud comme page à faire reculer,
  // et `closest` plantait.
  const node = contentEl() as Node | undefined
  if (!node) return undefined
  if (node.nodeType === Node.ELEMENT_NODE) return node as HTMLElement
  return ((node as CharacterData).nextElementSibling as HTMLElement | null) ?? undefined
}
defineExpose({ dismiss, el })
</script>

<template>
  <DialogRoot :open="localOpen" :modal="true" @update:open="onOpenChange">
    <DialogPortal>
      <!-- Le flou porte la profondeur : l'écran de dessous reste lisible comme
           décor sans plus rien réclamer. Deux modes s'en passent, et pas par
           oubli : `card` doit laisser voir NETTEMENT la page qui recule
           derrière elle, c'est toute la mise en scène iOS, et `fullscreen`
           cache son voile sous un panneau opaque, où le flou ne serait qu'une
           passe GPU pleine page pour rien. -->
      <DialogOverlay
        class="app-sheet-backdrop fixed inset-0 z-[19990]"
        :class="[
          mode === 'card' ? 'bg-transparent' : 'bg-black/30',
          mode === 'sheet' || mode === 'centered' ? 'backdrop-blur-sm' : '',
          backdropClass,
        ]"
        :style="veilStyle"
      />
      <DialogContent
        ref="contentRef"
        :class="[
          'app-sheet-panel fixed z-[19991] flex flex-col outline-none',
          transparent ? 'app-sheet-panel--bare' : 'overflow-hidden',
          fade ? 'app-sheet-panel--fade' : '',
          mode === 'centered'
            ? 'app-sheet-panel--centered left-1/2 top-1/2 max-h-[85vh] w-[90%] max-w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-[20px]'
            : mode === 'fullscreen'
              ? 'app-sheet-panel--full inset-0'
              : 'inset-x-0 bottom-0 mx-auto w-full max-w-[560px] rounded-t-[20px]',
          mode === 'card' ? 'app-sheet-panel--card' : '',
          panelClass,
        ]"
        :style="panelStyle"
        @pointer-down-outside="(e: Event) => { if (!backdropDismiss) e.preventDefault() }"
        @interact-outside="(e: Event) => { if (!backdropDismiss) e.preventDefault() }"
        @touchstart.passive="onDragStart"
        @touchmove="onDragMove"
        @touchend="onDragEnd"
        @touchcancel="onDragCancel"
        @click.capture="onPanelClick"
      >
        <VisuallyHidden>
          <DialogTitle>{{ ariaLabel ?? '' }}</DialogTitle>
          <DialogDescription />
        </VisuallyHidden>

        <!-- La poignée n'est plus qu'un repère visuel : le geste est écouté
             sur tout le panneau, elle n'a plus rien à capter elle-même. -->
        <div v-if="mode === 'sheet'" class="flex h-6 shrink-0 items-center justify-center">
          <span class="h-1 w-9 rounded-full bg-line-strong" aria-hidden="true" />
        </div>

        <div class="flex min-h-0 flex-1 flex-col">
          <slot />
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<!-- Non scopé : les appelants stylent leur contenu, et le panneau est
     téléporté hors du composant. Fond dégradé blanc vers crème, comme avant. -->
<style>
.app-sheet-panel {
  background: linear-gradient(to bottom, var(--card) 0%, var(--color-warm) 60%);
}

/* L'appelant peint sa propre carte : le panneau ne fait que la positionner. */
.app-sheet-panel--bare {
  background: none;
}

.app-sheet-panel--full {
  border-radius: 0;
}

/* Le fondu l'emporte sur la montée : déclaré APRÈS, à spécificité égale+1. */
@keyframes app-sheet-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Le clavier ne rétrécit plus la webview : une feuille est `position: fixed`,
   donc calée sur le viewport et non sur le corps qui, lui, a déjà rendu la
   place. Elle doit remonter d'elle-même, sinon la saisie se fait derrière le
   clavier. Le `:not()` porte la spécificité à deux classes, de quoi passer
   devant l'utilitaire `bottom-0` de Tailwind. */
.app-sheet-panel:not(.app-sheet-panel--centered) {
  bottom: var(--keyboard-height, 0px);
}

/* Une modale centrée n'a pas de `bottom` : lui en donner un l'étirerait. On
   recentre dans l'espace qui reste au-dessus du clavier. */
.app-sheet-panel.app-sheet-panel--centered {
  top: calc(50% - var(--keyboard-height, 0px) / 2);
  max-height: calc(85dvh - var(--keyboard-height, 0px));
}

/* La carte est ancrée en bas et laisse libre la barre d'état plus 10px, comme
   la modale carte iOS. `top: 12px` en dur passait DERRIÈRE l'encoche : sur un
   iPhone récent la barre d'état fait une soixantaine de points. */
.app-sheet-panel--card {
  top: calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 10px);
  border-radius: 10px 10px 0 0;
}

/* Les appelants mettent leur `AppContent` dans le panneau pour le
   défilement : sans hauteur définie il se replierait sur zéro. */
.app-sheet-panel .app-content {
  flex: 1 1 auto;
  min-height: 0;
}

@keyframes app-sheet-rise {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes app-sheet-backdrop-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.app-sheet-panel[data-state="open"] {
  animation: app-sheet-rise 280ms cubic-bezier(0.32, 0.72, 0, 1);
}

/* Images-clés dans index.css, partagées avec AppAlert. La règle reste ici :
   déclarée APRÈS la montée par défaut, à spécificité égale, elle gagne. */
.app-sheet-panel--centered[data-state="open"] {
  animation: modal-pop-in var(--modal-pop-duration) var(--modal-pop-easing);
}

.app-sheet-backdrop[data-state="open"] {
  animation: app-sheet-backdrop-in 280ms ease-out;
}

.app-sheet-panel--fade[data-state="open"] {
  animation: app-sheet-fade-in 300ms ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .app-sheet-panel[data-state="open"],
  .app-sheet-panel--centered[data-state="open"],
  .app-sheet-panel--fade[data-state="open"],
  .app-sheet-backdrop[data-state="open"] {
    animation: none;
  }
}
</style>
